import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/auth.css";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";

const EyeIcon = ({ off }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {off ? (
      <>
        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
        <path d="M6.61 6.61A13.52 13.52 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
        <line x1="2" y1="2" x2="22" y2="22" />
      </>
    ) : (
      <>
        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

export default function Login() {
  const { login } = useAuth();
  const { t, lang, changeLang } = useI18n();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(mobile.trim(), password);
      navigate("/");
    } catch (err) {
      // Only show credentials error if the server returned a 400 or 401.
      // Other error statuses (like 500, 502, 504 from the proxy) indicate a server issue.
      if (err.response && (err.response.status === 400 || err.response.status === 401)) {
        setError(t("loginError"));
      } else {
        setError(t("serverError"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-split">
      {/* Left brand panel */}
      <div className="auth-brand">
        <div className="auth-logo">
          <img src="/logo.png" alt="CCMC Logo" style={{ width: "64px", height: "64px", borderRadius: "50%", marginRight: "12px", border: "2.5px solid rgba(255,255,255,0.45)", objectFit: "cover" }} />
          <div>
            <div className="auth-logo-name">{t("appName")}</div>
          </div>
        </div>

        <p className="auth-blurb">{t("loginBlurb")}</p>

        <ul className="auth-features">
          {[t("loginFeat1"), t("loginFeat2"), t("loginFeat3")].map((f) => (
            <li key={f}>
              <span className="chk">✓</span>
              {f}
            </li>
          ))}
        </ul>
      </div>

      {/* Right form panel */}
      <div className="auth-form-side">
        <form className="auth-form" onSubmit={submit}>
          {/* Mobile-only compact branding */}
          <div className="mobile-brand-header">
            <img src="/logo.png" alt="CCMC Logo" style={{ width: "40px", height: "40px", borderRadius: "50%", marginRight: "8px", border: "1.5px solid #1e3a8a", objectFit: "cover" }} />
            <div>
              <div className="app-title-small">{t("appName")}</div>
            </div>
          </div>

          <h2>{t("signIn")}</h2>

          {error && <div className="error-text">{error}</div>}

          <div className="field">
            <label>{t("mobile")}</label>
            <input
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder={t("mobile")}
              type="text"
              maxLength={15}
              required
            />
          </div>
          <div className="field">
            <label>{t("password")}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: "100%", padding: 13, fontSize: 14 }}
            disabled={busy}
          >
            {busy ? t("loading") : `${t("signIn")} →`}
          </button>

          <div style={{ textAlign: "center", marginTop: 14 }}>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => changeLang(lang === "en" ? "ta" : "en")}
            >
              {lang === "en" ? "தமிழில் காண்க" : "View in English"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
